import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Globe, TrendingUp, Users, Heart, Leaf } from 'lucide-react';

const Home = () => {
  const stats = [
    { value: '5000+', label: 'Metric Tons Sold Annually', icon: TrendingUp },
    { value: '300', label: 'MT Processing Capacity/Day', icon: Award },
    { value: 'PAN India', label: 'Market Presence', icon: Globe },
    { value: '3rd Gen', label: 'Business Legacy', icon: Users },
  ];

  const values = [
    { title: 'Quality & Trust', icon: Award, description: 'Premium quality in every grain' },
    { title: 'Sustainable Future', icon: Leaf, description: 'Eco-friendly farming practices' },
    { title: 'Growth & Innovation', icon: TrendingUp, description: 'Continuous improvement' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.pexels.com/photos/17052142/pexels-photo-17052142.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/50"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Premium Basmati Rice
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 mt-2">
              For Discerning Palates
            </span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            A Strong Belief, A Bold Dream. India's Premier Export and Rice Trading House.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/products" 
              className="group px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-500 text-white rounded-full font-semibold text-lg hover:from-amber-500 hover:to-yellow-400 transition-all duration-300 shadow-xl hover:shadow-2xl flex items-center justify-center"
              data-testid="explore-products-btn"
            >
              Explore Products
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full font-semibold text-lg border-2 border-white/50 hover:bg-white/20 transition-all duration-300"
              data-testid="contact-us-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-amber-600 via-yellow-500 to-amber-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center text-white">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-white/20 rounded-full backdrop-blur-sm">
                      <Icon size={32} />
                    </div>
                  </div>
                  <h3 className="text-4xl font-bold mb-2">{stat.value}</h3>
                  <p className="text-lg text-amber-50">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Crafting Excellence in
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                  Every Grain
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Dreampack Solutions is a 3rd generation family business and India's premier export and rice trading house specializing in premium Basmati rice. Under our flagship brand Amyrah, we deliver excellence to discerning customers worldwide.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                With a commitment to quality, sustainability, and innovation, we process 300 metric tons daily and serve markets across India and international destinations, especially in GCC countries.
              </p>
              <Link 
                to="/about" 
                className="inline-flex items-center px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-all duration-300"
                data-testid="learn-more-btn"
              >
                Learn More About Us
                <ArrowRight className="ml-2" size={18} />
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1611501355758-0d8b8208e1ab" 
                alt="Golden rice field" 
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <Heart className="text-amber-600 mb-2" size={32} />
                <p className="text-sm font-semibold text-gray-900">Quality is our Promise</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Premium
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-yellow-500">
                Amyrah Collection
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the finest Basmati rice varieties, carefully selected and packaged for your delight
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ProductCard 
              image="https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/sj8o8lv1_EVERYDAY1KG.MAIN.png"
              title="Amyrah Everyday"
              description="Perfect for daily cooking with premium quality and aroma"
              color="from-red-500 to-rose-600"
            />
            <ProductCard 
              image="https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/4fzai17a_EXCEL1KG.MAIN.png"
              title="Amyrah Excel"
              description="Superior quality for special occasions and festivities"
              color="from-blue-600 to-indigo-600"
            />
            <ProductCard 
              image="https://customer-assets.emergentagent.com/job_pack-preview-1/artifacts/qaiy3b7m_SUPREME1KG.MAIN.png"
              title="Amyrah Supreme"
              description="The ultimate luxury experience with premium quality"
              color="from-cyan-500 to-teal-600"
            />
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/products" 
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-600 to-yellow-500 text-white rounded-full font-semibold hover:from-amber-500 hover:to-yellow-400 transition-all duration-300 shadow-lg hover:shadow-xl"
              data-testid="view-all-products-btn"
            >
              View All Products
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Built on a foundation of integrity, quality, and sustainable practices
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center p-8 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl hover:shadow-xl transition-all duration-300">
                  <div className="inline-flex p-4 bg-gradient-to-r from-amber-600 to-yellow-500 rounded-full mb-4">
                    <Icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-24 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(217, 119, 6, 0.9), rgba(234, 179, 8, 0.9)), url('https://images.pexels.com/photos/6086557/pexels-photo-6086557.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Experience Premium Quality?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Partner with India's premier Basmati rice exporters for unmatched quality and service
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-amber-600 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-xl"
              data-testid="get-in-touch-btn"
            >
              Get In Touch
            </Link>
            <a 
              href="https://wa.me/919810137856" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-green-600 text-white rounded-full font-semibold text-lg hover:bg-green-700 transition-all duration-300 shadow-xl"
              data-testid="whatsapp-btn"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

const ProductCard = ({ image, title, description, color }) => {
  return (
    <div className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
      <div className="relative h-80 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-8">
        <img 
          src={image} 
          alt={title} 
          className="h-full w-auto object-contain group-hover:scale-110 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <h3 className={`text-2xl font-bold mb-3 bg-gradient-to-r ${color} bg-clip-text text-transparent`}>
          {title}
        </h3>
        <p className="text-gray-600 mb-4">{description}</p>
        <Link 
          to="/products" 
          className="text-amber-600 font-semibold hover:text-amber-700 inline-flex items-center"
        >
          Learn More
          <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
        </Link>
      </div>
    </div>
  );
};

export default Home;